import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { cycleApi } from '../../services/api';
import { PageHeader, EmptyState, Spinner, Modal } from '../../components/shared/index';
import toast from 'react-hot-toast';
import { PlusIcon } from '@heroicons/react/24/outline';

const STATUS_COLORS = {
  draft: 'bg-gray-100 text-gray-600', active: 'bg-blue-100 text-blue-700',
  scoring: 'bg-yellow-100 text-yellow-700', calibration: 'bg-purple-100 text-purple-700',
  locked: 'bg-orange-100 text-orange-700', published: 'bg-green-100 text-green-700',
};

function CycleForm({ onSave, onClose }) {
  const [form, setForm] = useState({ name:'', type:'annual', start_date:'', end_date:'', company_growth_pct:'' });
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  return (
    <form onSubmit={e => { e.preventDefault(); onSave(form); }} className="space-y-4">
      <div><label className="label">Cycle name *</label><input required className="input" value={form.name} onChange={set('name')} placeholder="e.g. H1 2025 Annual Review" /></div>
      <div className="grid grid-cols-2 gap-4">
        <div><label className="label">Type</label>
          <select className="input" value={form.type} onChange={set('type')}>
            {['annual','mid_year','quarterly','project','probation','promotion'].map(t => <option key={t} value={t}>{t.replace('_',' ')}</option>)}
          </select>
        </div>
        <div><label className="label">Company growth %</label><input type="number" step="0.1" className="input" value={form.company_growth_pct} onChange={set('company_growth_pct')} placeholder="e.g. 15" /></div>
        <div><label className="label">Start date *</label><input required type="date" className="input" value={form.start_date} onChange={set('start_date')} /></div>
        <div><label className="label">End date *</label><input required type="date" className="input" value={form.end_date} onChange={set('end_date')} /></div>
        <div><label className="label">Review deadline</label><input type="date" className="input" value={form.review_deadline||''} onChange={set('review_deadline')} /></div>
        <div><label className="label">Calibration deadline</label><input type="date" className="input" value={form.calibration_deadline||''} onChange={set('calibration_deadline')} /></div>
      </div>
      <div className="flex justify-end gap-3 pt-2">
        <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
        <button type="submit" className="btn-primary">Create cycle</button>
      </div>
    </form>
  );
}

export default function CycleList() {
  const qc = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const { data, isLoading } = useQuery({ queryKey: ['cycles'], queryFn: () => cycleApi.list().then(r => r.data) });
  const createMut = useMutation({
    mutationFn: cycleApi.create,
    onSuccess:  () => { qc.invalidateQueries(['cycles']); setShowForm(false); toast.success('Cycle created'); },
    onError:    (e) => toast.error(e.response?.data?.message || 'Failed'),
  });
  const activateMut = useMutation({ mutationFn: (id) => cycleApi.activate(id), onSuccess: () => qc.invalidateQueries(['cycles']) });
  const scoreMut    = useMutation({ mutationFn: (id) => cycleApi.triggerScoring(id), onSuccess: () => { qc.invalidateQueries(['cycles']); toast.success('Scoring started'); } });

  const cycles = data?.data || [];
  return (
    <div className="p-6 max-w-5xl mx-auto">
      <PageHeader title="Performance Cycles" subtitle="Manage review cycles and calibration"
        actions={<button onClick={() => setShowForm(true)} className="btn-primary"><PlusIcon className="w-4 h-4" />New cycle</button>} />
      {isLoading ? <div className="flex justify-center py-20"><Spinner /></div>
      : cycles.length === 0 ? <EmptyState title="No cycles yet" description="Create your first performance review cycle." action={<button onClick={() => setShowForm(true)} className="btn-primary">Create cycle</button>} />
      : (
        <div className="space-y-3">
          {cycles.map(c => (
            <div key={c.id} className="card p-5 flex items-center gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-3">
                  <h3 className="font-semibold text-gray-900">{c.name}</h3>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[c.status]||'bg-gray-100 text-gray-600'}`}>{c.status}</span>
                  <span className="px-2 py-0.5 rounded-full text-xs bg-gray-50 text-gray-500 border border-gray-100 capitalize">{c.type.replace('_',' ')}</span>
                </div>
                <p className="text-sm text-gray-500 mt-1">{c.start_date} → {c.end_date}{c.company_growth_pct != null ? ` · Growth: ${c.company_growth_pct}%` : ''}</p>
              </div>
              <div className="flex gap-2 shrink-0">
                {c.status === 'draft' && <button onClick={() => activateMut.mutate(c.id)} className="btn-secondary btn-sm">Activate</button>}
                {c.status === 'active' && <button onClick={() => scoreMut.mutate(c.id)} className="btn-primary btn-sm">Run scoring</button>}
                {['calibration','locked'].includes(c.status) && (
                  <Link to={`/cycles/${c.id}/calibration`} className="btn-primary btn-sm">Open calibration</Link>
                )}
                <Link to={`/cycles/${c.id}`} className="btn-secondary btn-sm">View</Link>
              </div>
            </div>
          ))}
        </div>
      )}
      <Modal open={showForm} onClose={() => setShowForm(false)} title="New performance cycle" size="lg">
        <CycleForm onSave={d => createMut.mutate(d)} onClose={() => setShowForm(false)} />
      </Modal>
    </div>
  );
}
